import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import type { Outlet, OutletStatusInfo } from '@photobooth/types';

export function useOutlets() {
  const [outlets, setOutlets] = useState<Outlet[]>([]);
  const [statuses, setStatuses] = useState<Map<string, OutletStatusInfo>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOutlets = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.outlets.getAll();
      if (response.success && response.data) {
        // Handle nested data structure from API client
        const outletsData = (response.data as any).data || response.data;
        setOutlets(Array.isArray(outletsData) ? outletsData : []);
      } else {
        setError('Failed to fetch outlets');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch outlets');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStatuses = async () => {
    try {
      const response = await api.outlets.getAllStatuses();
      if (response.success && response.data) {
        // Handle nested data structure from API client
        const statusesData = (response.data as any).data || response.data;
        const statusArray = Array.isArray(statusesData) ? statusesData : [];
        setStatuses(prev => {
          const next = new Map(prev);
          statusArray.forEach(status => {
            const previousStatus = prev.get(status.outletId);

            // Prefer the most recent lastHeartbeat between previous (WebSocket)
            // and the REST response, so polling cannot jump the timestamp
            // backwards (e.g. from "1 minute ago" to "2 days ago").
            const incomingHeartbeat = new Date(status.lastHeartbeat);
            const previousHeartbeat = previousStatus
              ? new Date(previousStatus.lastHeartbeat)
              : null;
            const lastHeartbeat =
              previousHeartbeat && previousHeartbeat > incomingHeartbeat
                ? previousHeartbeat
                : incomingHeartbeat;

            next.set(status.outletId, {
              ...status,
              lastHeartbeat,
              // Preserve live deviceCounts from WebSocket updates so that
              // periodic REST polling does not reset them back to zero.
              deviceCounts: previousStatus?.deviceCounts ?? status.deviceCounts,
            });
          });
          return next;
        });
      }
    } catch (err) {
      console.error('Failed to fetch outlet statuses:', err);
    }
  };

  useEffect(() => {
    fetchOutlets();
    fetchStatuses();

    // Refresh statuses every 30 seconds
    const interval = setInterval(fetchStatuses, 30000);
    return () => clearInterval(interval);
  }, []);

  const updateOutletStatus = (status: OutletStatusInfo) => {
    setStatuses(prev => new Map(prev).set(status.outletId, status));
  };

  return {
    outlets,
    statuses,
    isLoading,
    error,
    refetch: fetchOutlets,
    updateOutletStatus,
  };
}
